import { useSelector } from 'react-redux';
import { useGetMenuStructureQuery } from '../services/dashboardApi';

/**
 * Custom hook to check user permissions
 * @returns {Object} - Permission checking utilities
 */
export const usePermissions = () => {
  const { user } = useSelector((state) => state.auth);
  const { data: menuData } = useGetMenuStructureQuery();

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission string in format 'module:action' (e.g., 'roles:create')
   * @returns {boolean} - True if user has the permission
   */
  const hasPermission = (permission) => {
    if (!menuData?.permissions) return false;
    return menuData.permissions.some(p => p === permission);
  };

  /**
   * Check if user has any of the provided permissions
   * @param {string[]} permissions - Array of permission strings
   * @returns {boolean} - True if user has any of the permissions
   */
  const hasAnyPermission = (permissions) => {
    if (!menuData?.permissions || !permissions.length) return false;
    return permissions.some(permission => menuData.permissions.includes(permission));
  };

  /**
   * Check if user has all of the provided permissions
   * @param {string[]} permissions - Array of permission strings
   * @returns {boolean} - True if user has all permissions
   */
  const hasAllPermissions = (permissions) => {
    if (!menuData?.permissions || !permissions.length) return false;
    return permissions.every(permission => menuData.permissions.includes(permission));
  };

  /**
   * Check if user can create items for a module
   * @param {string} module - Module name (e.g., 'roles', 'employees', 'departments')
   * @returns {boolean} - True if user can create items in the module
   */
  const canCreate = (module) => {
    return hasAnyPermission([`${module}:create`, `${module}s:create`]);
  };

  /**
   * Check if user can read/view items for a module
   * @param {string} module - Module name (e.g., 'roles', 'employees', 'departments')
   * @returns {boolean} - True if user can read items in the module
   */
  const canRead = (module) => {
    return hasAnyPermission([`${module}:read`, `${module}:view`, `${module}s:read`, `${module}s:view`]);
  };

  /**
   * Check if user can update/edit items for a module
   * @param {string} module - Module name (e.g., 'roles', 'employees', 'departments')
   * @returns {boolean} - True if user can update items in the module
   */
  const canUpdate = (module) => {
    return hasAnyPermission([`${module}:update`, `${module}:edit`, `${module}s:update`, `${module}s:edit`]);
  };

  /**
   * Check if user can delete items for a module
   * @param {string} module - Module name (e.g., 'roles', 'employees', 'departments')
   * @returns {boolean} - True if user can delete items in the module
   */
  const canDelete = (module) => {
    return hasAnyPermission([`${module}:delete`, `${module}s:delete`]);
  };

  /**
   * Check if user can approve items for a module
   * @param {string} module - Module name (e.g., 'roles', 'employees', 'departments')
   * @returns {boolean} - True if user can approve items in the module
   */
  const canApprove = (module) => {
    return hasAnyPermission([`${module}:approve`, `${module}s:approve`]);
  };

  /**
   * Get all user permissions as an array
   * @returns {string[]} - Array of permission strings
   */
  const getUserPermissions = () => {
    return menuData?.permissions || [];
  };

  return {
    user,
    permissions: getUserPermissions(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canApprove,
  };
};
